import classNames from "classnames";
import React, { FunctionComponent, useEffect, useState } from "react";

export interface SidebarProps {
  show: boolean;
  toggle: (state: boolean) => void;
  size?: "sm" | "md" | "lg";
  title?: string;
  closeable?: boolean;
  noBg?: boolean;
  bgClose?: boolean;
  className?: string;
  headerAddon?: React.ReactNode;
}

const SideMenu: FunctionComponent<SidebarProps> = ({
  show,
  toggle,
  size = "md",
  title,
  children,
  closeable = true,
  noBg = false,
  bgClose = true,
  className = "",
  headerAddon,
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    if (show) {
      setIsVisible(true); // Show the sidebar immediately
    } else {
      // Delay hiding the sidebar to allow the animation to play
      const timer = setTimeout(() => setIsVisible(false), 300); // Match the animation duration
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Disable background scrolling when the sidenav is open
  useEffect(() => {
    if (isVisible) {
      document.documentElement.classList.add("overflow-hidden"); // Add class to <html>
    } else {
      document.documentElement.classList.remove("overflow-hidden"); // Remove class from <html>
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.classList.remove("overflow-hidden");
    };
  }, [isVisible]);

  const sizeClasses = {
    sm: "w-64",
    md: "w-80",
    lg: "w-[320px] md:w-96",
  };

  const wrapperClasses = classNames(
    "fixed inset-0 z-[999999]",
    {
      "sidenav-wrapper-anim": show,
      "sidenav-wrapper-anim-rev": !show,
      "bg-black bg-opacity-70": !noBg,
      "bg-opacity-0": noBg,
    },
    className
  );

  const sidebarClasses = classNames("bg-white h-full fixed top-0", sizeClasses[size], {
    "sidenav-body-anim": show,
    "sidenav-body-anim-rev": !show,
  });

  return (
    <>
      {isVisible && (
        <div className={wrapperClasses} onClick={(e) => (bgClose && closeable ? toggle(false) : null)}>
          <div className={sidebarClasses} onClick={(e) => e.stopPropagation()}>
            {/* Sidebar Header */}

            <div className="pt-5 px-6 flex flex-col gap-y-5">
              <div className="flex items-center justify-between">
              {headerAddon}
              {closeable && (
                <button
                  type="button"
                  onClick={() => toggle(false)}
                  className="bg-grey-fields-100 p-2 rounded-full text-black-secondary hover:text-black-placeholder focus:outline-none justify-self-end ml-auto"
                >
                  {/* Close Icon */}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              </div>
              <hr className="border border-grey-divider"></hr>
            </div>

            {/* Sidebar Content */}
            <div className="px-6.25 py-3 overflow-y-auto h-full">{children}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideMenu;
